import {Button, Checkbox, Collapse, CollapseProps, Dropdown, Flex, Input, InputNumber, Space, theme} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight, faBullseye, faCaretDown, faCaretRight, faMultiply} from "@fortawesome/free-solid-svg-icons";
import {interpolators, selectedInterpolator} from "../../common/Interpolators";
import React, {ReactNode, useCallback, useEffect} from "react";
import {Animation} from "../../../feature/project/Shot";
import {useParams} from "react-router-dom";

export const AnimationForm = (props: {
    animation: Animation,
    pivotPoint?: boolean,
    wrapper: (children: ReactNode) => ReactNode,
    onSave: (animation: Animation) => void,
    onClose: () => void,
    onSelectPivotPoint: (select: boolean) => void
}) => {

    const {projectId} = useParams()
    const [animation, setAnimation] = React.useState<Animation | undefined>(props.animation)
    const [change, setChange] = React.useState(0)
    const [c3Width, setC3Width] = React.useState(0)
    const {token} = theme.useToken();
    const [activeKeys, setActiveKeys] = React.useState<string[] | undefined>(undefined)
    const [playInterpolator, setPlayInterpolator] = React.useState<string | undefined>(undefined)
    const [initial, setInitial] = React.useState(true)

    const panelStyle = {
        marginBottom: 10,
        background: token.colorFillAlter,
        borderRadius: 6,
        border: 'none',
        fontWeight: 700,
    }


    useEffect(() => {
        setChange(prev => prev + 1)
    }, [props.pivotPoint]);

    useEffect(() => {

        if (initial) {
            setActiveKeys(getActiveKeys(animation))
            setInitial(false)
        }
        setAnimation(props.animation)
    }, [props.animation])

    useEffect(() => {
        if (animation?.id) {
            props.onSave(animation)
        }
    }, [change]);

    const column3elementRef = useCallback((node: HTMLDivElement) => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            setC3Width(node.clientWidth || 0)
        });
        resizeObserver.observe(node);
    }, []);

    const pivotPointPanel = <Space direction={'vertical'}>
        <Space>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.pivotX}
                addonBefore={"X"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, pivotX: value})
                }}
            />
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.pivotY}
                addonBefore={"Y"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, pivotY: value})
                }}
            />
            <Button type={props.pivotPoint ? 'primary' : 'text'}
                    icon={<FontAwesomeIcon icon={faBullseye}/>}
                    onClick={() => props.onSelectPivotPoint(!props.pivotPoint)}/>
        </Space>
    </Space>

    const translatePanel = <Space direction={'vertical'}>
        <Space.Compact>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.translateFromX}
                addonBefore={"X"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, translateFromX: value})
                }}
            />
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.translateToX}
                addonBefore={<FontAwesomeIcon icon={faArrowRight}/>}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, translateToX: value})
                }}
            />
        </Space.Compact>
        <Space.Compact>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.translateFromY}
                addonBefore={"Y"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, translateFromY: value})
                }}
            />
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.translateToY}
                addonBefore={<FontAwesomeIcon icon={faArrowRight}/>}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, translateToY: value})
                }}
            />
        </Space.Compact>
    </Space>

    const rotatePanel = <Space direction={'vertical'}>
        <Space.Compact>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.rotateFromX}
                addonBefore={"X"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, rotateFromX: value})
                }}
                min={-360}
                max={360}/>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.rotateToX}
                addonBefore={<FontAwesomeIcon icon={faArrowRight}/>}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, rotateToX: value})
                }}
                min={-360}
                max={360}/>
        </Space.Compact>
        <Space.Compact>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.rotateFromY}
                addonBefore={"Y"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, rotateFromY: value})
                }}
                min={-360}
                max={360}/>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.rotateToY}
                addonBefore={<FontAwesomeIcon icon={faArrowRight}/>}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, rotateToY: value})
                }}
                min={-360}
                max={360}/>
        </Space.Compact>
        <Space.Compact>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.rotateFromZ}
                addonBefore={"Z"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, rotateFromZ: value})
                }}
                min={-360}
                max={360}/>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.rotateToZ}
                addonBefore={<FontAwesomeIcon icon={faArrowRight}/>}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, rotateToZ: value})
                }}
                min={-360}
                max={360}/>
        </Space.Compact>
    </Space>

    const scalePanel = <Space direction={'vertical'}>
        <Space.Compact>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.scaleFromX}
                addonBefore={"X"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, scaleFromX: value})
                }}
                min={0} step={0.1}/>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.scaleToX}
                addonBefore={<FontAwesomeIcon icon={faArrowRight}/>}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, scaleToX: value})
                }}
                min={0} step={0.1}/>
        </Space.Compact>
        <Space.Compact>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.scaleFromY}
                addonBefore={"Y"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, scaleFromY: value})
                }}
                min={0} step={0.1}/>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.scaleToY}
                addonBefore={<FontAwesomeIcon icon={faArrowRight}/>}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, scaleToY: value})
                }}
                min={0} step={0.1}/>
        </Space.Compact>
        <Space.Compact>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.scaleFromZ}
                addonBefore={"Z"}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, scaleFromZ: value})
                }}
                min={0} step={0.1}/>
            <InputNumber
                onPressEnter={(e) => {
                    e.currentTarget.blur()
                }}
                onBlur={() => {
                    setChange(prev => prev + 1)
                }}
                controls={false}
                value={animation?.scaleToZ}
                addonBefore={<FontAwesomeIcon icon={faArrowRight}/>}
                changeOnBlur={true}
                onChange={(value: number | null) => {
                    setAnimation({...animation, scaleToZ: value})
                }}
                min={0} step={0.1}/>
        </Space.Compact>
    </Space>

    const opacityPanel = <Space.Compact>
        <InputNumber
            onPressEnter={(e) => {
                e.currentTarget.blur()
            }}
            onBlur={() => {
                setChange(prev => prev + 1)
            }}
            controls={false}
            value={animation?.alphaFrom}
            addonBefore={"%"}
            changeOnBlur={true}
            onChange={(value: number | null) => {
                setAnimation({...animation, alphaFrom: value})
            }}
            min={0}
            max={100}/>
        <InputNumber
            onPressEnter={(e) => {
                e.currentTarget.blur()
            }}
            onBlur={() => {
                setChange(prev => prev + 1)
            }}
            controls={false}
            value={animation?.alphaTo}
            addonBefore={<FontAwesomeIcon icon={faArrowRight}/>}
            changeOnBlur={true}
            onChange={(value: number | null) => {
                setAnimation({...animation, alphaTo: value})
            }}
            min={0}
            max={100}/>
    </Space.Compact>

    const delayPanel = <Space direction={'vertical'}>
        <InputNumber
            onPressEnter={(e) => {
                e.currentTarget.blur()
            }}
            onBlur={() => {
                setChange(prev => prev + 1)
            }}
            controls={false}
            value={animation?.startDelay}
            changeOnBlur={true}
            onChange={(value: number | null) => {
                setAnimation({...animation, startDelay: value})
            }}
            addonBefore={"Start, ms"}
            min={0}/>
        <InputNumber
            onPressEnter={(e) => {
                e.currentTarget.blur()
            }}
            onBlur={() => {
                setChange(prev => prev + 1)
            }}
            controls={false}
            value={animation?.repeatDelay}
            changeOnBlur={true}
            onChange={(value: number | null) => {
                setAnimation({...animation, repeatDelay: value})
            }}
            addonBefore={"Repeat, ms"}
            min={0}/>
    </Space>

    const repeatPanel = <Space direction={'vertical'}>
        <Checkbox checked={animation?.repeat === -1} onChange={(e) => {
            setAnimation({
                ...animation,
                repeat: e.target.checked ? -1 : null
            })
            setChange(prev => prev + 1)
        }}>Infinity</Checkbox>
        <InputNumber
            onPressEnter={(e) => {
                e.currentTarget.blur()
            }}
            onBlur={() => {
                setChange(prev => prev + 1)
            }}
            controls={false}
            value={animation?.repeat != null && animation?.repeat > 0 ? animation?.repeat : null}
            changeOnBlur={true}
            onChange={(value: number | null) => {
                setAnimation({...animation, repeat: value})
            }}
            addonBefore={"Times"}
            min={0}/>
    </Space>

    const reversePanel = <Checkbox checked={animation?.reverse === true} onChange={(e) => {
        setAnimation({
            ...animation,
            reverse: e.target.checked
        })
        setChange(prev => prev + 1)
    }
    }>Reverse animation</Checkbox>

    const durationPanel = <InputNumber
        onPressEnter={(e) => {
            e.currentTarget.blur()
        }}
        onBlur={() => {
            setChange(prev => prev + 1)
        }}
        controls={false}
        value={animation?.duration}
        addonBefore={"ms"}
        changeOnBlur={true}
        onChange={(value: number | null) => {
            setAnimation({...animation, duration: value})
        }}
        min={0}/>

    const interpolationPanel = <Dropdown trigger={['click']} menu={
        {
            items: interpolators.map(interpolator => {
                return {
                    ...interpolator,
                    label: <div
                        onMouseEnter={() =>
                            setPlayInterpolator(interpolator.key)
                        }
                        onMouseLeave={() => setPlayInterpolator(undefined)}
                    >
                        <div ref={column3elementRef} style={{
                            width: '100%',
                            height: 10,
                            display: 'flex',
                            alignItems: 'end',
                            position: 'relative'
                        }}>
                            <div style={{
                                width: playInterpolator === interpolator.key ? 7 : 0,
                                height: playInterpolator === interpolator.key ? 7 : 0,
                                bottom: 0,
                                left: 0,
                                position: 'absolute',
                                background: '#eee',
                                borderRadius: 4
                            }}></div>
                            <div style={{
                                width: playInterpolator === interpolator.key ? 7 : 0,
                                height: playInterpolator === interpolator.key ? 7 : 0,
                                bottom: 0,
                                right: 0,
                                position: 'absolute',
                                background: '#eee',
                                borderRadius: 4
                            }}></div>
                            <div style={{
                                height: playInterpolator === interpolator.key ? 1 : 0,
                                bottom: 3,
                                left: 0,
                                right: 0,
                                position: 'absolute',
                                background: '#eee',
                                borderRadius: 4
                            }}></div>
                            <div style={{
                                width: playInterpolator === interpolator.key ? 7 : 0,
                                height: playInterpolator === interpolator.key ? 7 : 0,
                                top: 0,
                                left: 0,
                                transitionTimingFunction: interpolator.value,
                                transitionProperty: playInterpolator === interpolator.key ? 'transform' : 'none',
                                transitionDuration: '1s',
                                transform: `translateX(${playInterpolator === interpolator.key ? c3Width - 7 : 0}px)`,
                                background: '#000',
                                borderRadius: 4
                            }}></div>
                        </div>
                        {interpolator.label}</div>
                }
            }),
            style: {
                maxHeight: 350,
                overflowY: 'auto', scrollbarWidth: "none"
            },
            onClick: (e) => {
                setAnimation({...animation, interpolator: e.key})
                setChange(prev => prev + 1)
            }
        }}
    >
        <Button style={{width: '100%'}}>
            <Flex justify={'space-between'} align={"center"} style={{width:"100%"}}>
                {selectedInterpolator(animation?.interpolator) ? (selectedInterpolator(animation?.interpolator)?.label || 'Select Interpolator') : 'Select Interpolator'}<FontAwesomeIcon
                icon={faCaretDown}/>
            </Flex>
        </Button>
    </Dropdown>

    const items: CollapseProps['items'] = [
        {
            key: '0',
            label: 'Pivot Point',
            style: panelStyle,
            children: pivotPointPanel
        },
        {
            key: '1',
            label: 'Translate',
            style: panelStyle,
            children: translatePanel
        },
        {
            key: '2',
            label: 'Rotate',
            style: panelStyle,
            children: rotatePanel
        },
        {
            key: '3',
            label: 'Scale',
            style: panelStyle,
            children: scalePanel
        },
        {
            key: '4',
            label: 'Opacity',
            style: panelStyle,
            children: opacityPanel
        },
        {
            key: '5',
            label: 'Delay',
            style: panelStyle,
            children: delayPanel
        },
        {
            key: '6',
            label: 'Repeat',
            style: panelStyle,
            children: repeatPanel
        },
        {
            key: '7',
            label: 'Reverse',
            style: panelStyle,
            children: reversePanel
        },
        {
            key: '8',
            label: 'Duration',
            style: panelStyle,
            children: durationPanel
        },
        {
            key: '9',
            label: 'Interpolation',
            style: panelStyle,
            children: interpolationPanel
        }
    ]

    return <>
        {animation && activeKeys ?
            props.wrapper(
                <>
                    <Flex gap={10}>
                        <Input value={animation?.title}
                               placeholder={'Title'}
                               onPressEnter={(e) => {
                                   e.currentTarget.blur()
                               }}

                               onBlur={() => {
                                   setChange(prev => prev + 1)
                               }}
                               onChange={(e) => setAnimation({...animation, title: e.target.value})}
                        />
                        <Button
                            icon={<FontAwesomeIcon icon={faMultiply}/>}
                            type={'text'}
                            onClick={() => {
                                props.onClose()
                                setAnimation(undefined)
                                setActiveKeys(undefined)
                            }}
                        />
                    </Flex>
                    <Collapse
                        key={animation.id + (activeKeys.join('') || '')}
                        defaultActiveKey={activeKeys}
                        style={{background: 'transparent'}}
                        bordered={false}
                        items={items}
                        expandIcon={({isActive}) =>
                            <FontAwesomeIcon
                                icon={faCaretRight}
                                transform={{rotate: isActive ? 90 : 0}}
                            />}
                    />
                    {!animation.id ? <Button style={{width: '100%'}} type={'primary'} onClick={() => {
                        if (projectId && animation) {
                            props.onSave(animation)
                            props.onClose()
                            setAnimation(undefined)
                            setActiveKeys(undefined)
                            setInitial(true)
                        }
                    }}>Add Animation</Button> : ''}
                </>)
            : ''}
    </>
}

export function compareAnimations(origin: Animation | undefined, animation: Animation | undefined): boolean {
    if (origin && animation) {
        return JSON.stringify(origin) === JSON.stringify(animation)
    }
    return false
}

function getActiveKeys(animation?: Animation): string[] {
    const activeKeys: string[] = []
    if (!animation || !animation.id) {
        return activeKeys
    }
    if (animation?.pivotY !== null || animation?.pivotX !== null) {
        activeKeys.push('0')
    }
    if (animation?.translateFromX !== null || animation?.translateToX !== null
        || animation?.translateFromY !== null || animation?.translateToY !== null
        || animation?.translateFromZ !== null || animation?.translateToZ !== null) {
        activeKeys.push('1')
    }
    if (animation?.rotateFromX !== null || animation?.rotateToX !== null
        || animation?.rotateFromY !== null || animation?.rotateToY !== null
        || animation?.rotateFromZ !== null || animation?.rotateToZ !== null) {
        activeKeys.push('2')
    }
    if (animation?.scaleFromX !== null || animation?.scaleToX !== null
        || animation?.scaleFromY !== null || animation?.scaleToY !== null
        || animation?.scaleFromZ !== null || animation?.scaleToZ !== null) {
        activeKeys.push('3')
    }
    if (animation?.alphaFrom !== null || animation?.alphaTo !== null) {
        activeKeys.push('4')
    }
    if (animation?.startDelay !== null || animation?.repeatDelay !== null) {
        activeKeys.push('5')
    }
    if (animation?.repeat !== null) {
        activeKeys.push('6')
    }
    if (animation?.reverse) {
        activeKeys.push('7')
    }
    if (animation?.duration !== null) {
        activeKeys.push('8')
    }
    if (animation?.interpolator !== null) {
        activeKeys.push('9')
    }

    return activeKeys
}
